import React from "react"
import { motion } from "framer-motion"
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react"
import { REPORT_COLORS, REPORT_RADIUS } from "./reportTheme"
import { fit } from "./reportUtils"

// Shared building blocks for the combined Report page's four tabs, so each
// tab (AI CMO, AI Reports, AI Forecasting, Social Comparison) renders through
// one consistent visual system instead of inheriting its source page's look.

export const ReportCard = ({ children, style, ...rest }) => (
  <div
    style={{
      backgroundColor: REPORT_COLORS.cardBg,
      border: `1px solid ${REPORT_COLORS.cardBorder}`,
      borderRadius: REPORT_RADIUS,
      padding: "clamp(14px, 4vw, 20px)",
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
)

export const ReportSectionHeader = ({ icon: Icon, eyebrow, title, action }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      {Icon && (
        <div
          style={{
            width: "clamp(26px, 8vw, 32px)",
            height: "clamp(26px, 8vw, 32px)",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: REPORT_COLORS.accentSoft,
            flexShrink: 0,
          }}
        >
          <Icon size={16} color={REPORT_COLORS.accent} />
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        {eyebrow && (
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: REPORT_COLORS.textMuted, marginBottom: 2 }}>
            {eyebrow}
          </div>
        )}
        <h3 style={{ margin: 0, fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 600, color: REPORT_COLORS.textPrimary }}>{title}</h3>
      </div>
    </div>
    {action}
  </div>
)

// direction: 'up' | 'down' | 'flat'. goodDirection: which direction counts as "good" (default 'up').
export const ReportDelta = ({ direction, value, goodDirection = "up" }) => {
  if (direction === "flat" || !direction) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: REPORT_COLORS.textMuted, fontSize: 12, fontWeight: 500 }}>
        <Minus size={12} /> {value}
      </span>
    )
  }
  const isGood = direction === goodDirection
  const color = isGood ? REPORT_COLORS.good : REPORT_COLORS.critical
  const Icon = direction === "up" ? ArrowUpRight : ArrowDownRight
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color, fontSize: 12, fontWeight: 600 }}>
      <Icon size={12} /> {value}
    </span>
  )
}

export const ReportStatTile = ({ label, value, delta, deltaDirection, goodDirection, sub }) => (
  <ReportCard style={{ padding: "clamp(12px, 3.5vw, 16px)" }}>
    <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: REPORT_COLORS.textMuted, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {label}
    </div>
    <div style={{ fontSize: "clamp(18px, 6vw, 26px)", fontWeight: 700, color: REPORT_COLORS.textPrimary, fontFamily: "monospace", marginBottom: delta ? 6 : 0, overflowWrap: "anywhere" }}>
      {value}
    </div>
    {delta && <ReportDelta direction={deltaDirection} value={delta} goodDirection={goodDirection} />}
    {sub && <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 6 }}>{sub}</div>}
  </ReportCard>
)

export const ReportEmptyState = ({ icon: Icon, title, description }) => (
  <ReportCard style={{ textAlign: "center", padding: "clamp(28px, 10vw, 48px) clamp(16px, 5vw, 24px)" }}>
    {Icon && <Icon size={32} color={REPORT_COLORS.textMuted} style={{ marginBottom: 12, opacity: 0.6 }} />}
    <div style={{ color: REPORT_COLORS.textPrimary, fontWeight: 600, marginBottom: 6 }}>{title}</div>
    {description && <div style={{ color: REPORT_COLORS.textMuted, fontSize: 13, maxWidth: 360, margin: "0 auto" }}>{description}</div>}
  </ReportCard>
)

const shimmer = {
  backgroundColor: "rgba(255,255,255,0.06)",
  borderRadius: 8,
}

export const ReportSkeleton = ({ tiles = 4 }) => (
  <div>
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(160)})`, gap: 12, marginBottom: 16 }}>
      {Array.from({ length: tiles }).map((_, i) => (
        <ReportCard key={i} style={{ padding: 16 }}>
          <motion.div animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 1.4, repeat: Infinity }} style={{ ...shimmer, height: 10, width: "50%", marginBottom: 12 }} />
          <motion.div animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0.1 }} style={{ ...shimmer, height: 24, width: "70%" }} />
        </ReportCard>
      ))}
    </div>
    <ReportCard style={{ height: 220 }}>
      <motion.div animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 1.4, repeat: Infinity }} style={{ ...shimmer, height: "100%", width: "100%" }} />
    </ReportCard>
  </div>
)

export const ReportBadge = ({ tone = "good", children }) => {
  const map = {
    good: [REPORT_COLORS.good, REPORT_COLORS.goodSoft],
    warning: [REPORT_COLORS.warning, REPORT_COLORS.warningSoft],
    serious: [REPORT_COLORS.serious, REPORT_COLORS.seriousSoft],
    critical: [REPORT_COLORS.critical, REPORT_COLORS.criticalSoft],
    neutral: [REPORT_COLORS.textSecondary, "rgba(255,255,255,0.06)"],
  }
  const [color, bg] = map[tone] || map.neutral
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "clamp(3px, 1vw, 4px) clamp(8px, 2.5vw, 10px)",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color,
        backgroundColor: bg,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  )
}
